import React from 'react';
import { Box, Typography, Tooltip, Stack, StackProps } from '@mui/material';
import NoPrgramFound from '../../../assets/no-program-found.svg';
import LoaderComponent from 'components/Loader';

import { ArrowDownward, ArrowUpward } from '@mui/icons-material';

import HighlightedText from 'components/GlobalSearch/HighlightText';
import FormattedNumber from '../FormattedNumber';
import { useTheme } from "@mui/material/styles";
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';

type KpiTileProps = {
  color?: string;
  bgColor?: string;
  reverseTrendColor?: boolean;
  isLoading: boolean;
  isInProgress?: boolean;
  title: string;
  titleKey?: string;
  titleFontSize?: string;
  tooltipLabel?: TooltipLabel;
  number?: number | string | string[] | number[] | null;
  units?: null | string | string[];
  unitIcon?: null | string;
  unitIconColor?: null | string;
  change?: string | number;
  changeType?: 'increase' | 'decrease' | string;
  isCost?: boolean;
  minTitleHeight?: `${number}px`;
  hasTooltip?: boolean;
  handleEvent?: () => void;
  ratioNumber?: {
    number: number | string;
    totalNumber: number | string;
    growthRate: number | string;
  };
  containerProps?: StackProps;
  selectedTitle?: string;
  sectorLabel?: string;
  isHappySignal?: boolean;
  subText?: string;
  lostTime?: string;
  showChangePercentage?: boolean;
};

const isSpecialReverseKpi = (titleKey?: string) =>
  titleKey === 'Inactive Users' || titleKey === 'Throttled';

const getTrendColor = (
  colors: any,
  change: string | number | undefined,
  changeType?: string,
  reverseTrendColor?: boolean,
  titleKey?: string,
) => {
  if (change == 0) return 'grey';

  if (isSpecialReverseKpi(titleKey)) {
    // increase is bad (red), decrease is good (green)
    return changeType === 'increase' ? colors.kpiTile?.text?.changeUp : colors.kpiTile?.text?.changeDown;
  }

  // Default logic: increase is good (green), decrease is bad (red)
  if (!reverseTrendColor) {
    return changeType === 'increase' ? colors.kpiTile?.text?.changeDown : colors.kpiTile?.text?.changeUp;
  }

  return changeType === 'decrease' ? colors.kpiTile?.text?.changeDown : colors.kpiTile?.text?.changeUp;
};

const getBorderStyle = (colors: any, selectedTitle?: string, titleKey?: string) => {
  if (!selectedTitle) return 'none';
  if (selectedTitle !== titleKey) return 'none';
  return `4px solid ${colors.kpiTile?.background?.border}`;
};

const shouldRenderNumber = (value: any) => value !== '' && value !== null;

const getChangeIconComponent = (changeType?: string) =>
  changeType === 'increase' ? ArrowUpward : ArrowDownward;

const KpiTile = ({
  color,
  bgColor,
  reverseTrendColor = false,
  isLoading = false,
  isInProgress = false,
  title = '',
  titleKey = '',
  titleFontSize = '',
  tooltipLabel,
  number = '',
  units = null,
  unitIcon = null,
  unitIconColor = null,
  change,
  changeType,
  minTitleHeight = '38px',
  isCost = false,
  hasTooltip = true,
  handleEvent,
  ratioNumber,
  containerProps,
  selectedTitle,
  sectorLabel = '',
  isHappySignal = false,
  subText = '',
  lostTime = '',
  showChangePercentage = false,
}: Readonly<KpiTileProps>) => {
  const theme = useTheme();
  const colors = theme.palette;

  const resolvedColor =
    color ?? colors.kpiTile?.text?.value;

  const resolvedBgColor =
    bgColor ?? colors.kpiTile?.background?.card;

  const changeColor = getTrendColor(colors, change, changeType, reverseTrendColor, titleKey);
  const borderBottom = getBorderStyle(colors, selectedTitle, titleKey);

  const isNumberArray = Array.isArray(number);
  const isUnitsArray = Array.isArray(units);

  const shouldShowSector = sectorLabel !== '';
  const shouldShowSubText = !isLoading && subText !== '';
  const shouldShowChange = showChangePercentage;

  const isInsufficientDataCase = (number == '' && units == null);

  const isClickable = !!handleEvent;

  const renderSingleNumber = () => {
    if (!shouldRenderNumber(number)) {
      if (isInsufficientDataCase) {
        return <Typography variant="bodyText">Insufficient Data</Typography>;
      }
      return null;
    }

    return (
      <>
        <FormattedNumber number={number as number | string} isCost={isCost} />
        {!isUnitsArray && units && (
          <Typography
            variant="widgetValueNew"
            sx={{
              ml: 1,
            }}
          >
            {units}
          </Typography>
        )}
      </>
    );
  };

  const renderArrayNumbers = () => {
    if (!isNumberArray) return null;

    return (number as Array<number | string | null>).map((item, index) => {
      if (!shouldRenderNumber(item)) return null;

      const unitForIndex =
        isUnitsArray && units && units[index] ? units[index] : null;

      return (
        <Stack
          direction="row"
          alignItems="baseline"
          key={`${item}-${index}`}
          ml={index !== 0 ? '10px' : 0}
        >
          <FormattedNumber number={item as number | string} isCost={isCost} />
          {unitForIndex ? (
            <Typography
              variant="widgetUnitNew"
              sx={{
                ...(unitForIndex === '%' && { ml: 0 }),
              }}
            >
              {unitForIndex}
            </Typography>
          ) : null}{' '}
        </Stack>
      );
    });
  };

  const renderNumbersBlock = () => {
    if (isNumberArray) return renderArrayNumbers();
    return renderSingleNumber();
  };

  const renderUnitIcon = () => {
    if (!unitIcon) return null;

    const renderGrowthRate = () => {
      if (!ratioNumber || ratioNumber.growthRate === undefined) return null;
      return (
        <Typography variant="widgetUnitNew" sx={{ color: unitIconColor }}>
          {ratioNumber.growthRate}%
        </Typography>
      );
    };

    if (unitIcon === 'upArrow') {
      return (
        <Box display="flex" gap={0} alignItems="baseline" sx={{ ml: '10px' }}>
          {renderGrowthRate()}
          <ArrowUpward sx={{ ml: '2px', color: unitIconColor, position: 'relative', top: '2px' }} />
        </Box>
      );
    }

    if (unitIcon === 'downArrow') {
      return (
        <Box display="flex" gap={0} alignItems="baseline" sx={{ ml: '10px' }}>
          {renderGrowthRate()}
          <ArrowDownward
            sx={{ ml: '2px', color: unitIconColor, position: 'relative', top: '2px' }}
          />
        </Box>
      );
    }

    return (
      <img
        src={unitIcon}
        alt="unit"
        style={{
          height: '24px',
          width: '24px',
          marginLeft: '10px',
        }}
      />
    );
  };

  const renderChangeSection = () => {
    if (!shouldShowChange) return null;

    const isZeroChange = (change ?? 0) === 0;
    const Icon = getChangeIconComponent(changeType);

    return (
      <Box display="flex" alignItems="center" mt={1} color={changeColor}>
        {!isZeroChange && change != 0 && <Icon fontSize="small" />}
        <Typography variant="bodyText" ml={0.5} sx={{ color: colors?.kpiTile?.text?.noChange }}>
          {isZeroChange ? '0%' : `${change ?? 0}%`} vs previous month
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      display="flex"
      flexGrow={1}
      height="100%"
      position="relative"
      {...(isClickable && { onClick: handleEvent })}
    >
      <Stack
        minHeight="100px"
        pt={2}
        bgcolor={resolvedBgColor}
        fontFamily="Montserrat"
        borderRadius="5px"
        flexGrow={1}
        {...containerProps}
        sx={{
          position: 'relative',
          flexDirection: 'row',
          justifyContent: 'space-between',
          borderBottom,
          borderRadius: '5px',
          transition: 'all 0.3s ease-in-out',
          ...containerProps?.sx,
        }}
      >
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            flex: '30%',
          }}
        >
          <Stack
            direction="row"
            alignItems="start"
            marginTop={isHappySignal ? '35px' : ''}
            minHeight={minTitleHeight}
          >
            <Typography variant="bodyText" sx={{ p: '0 !important', color: colors.kpiTile?.text?.title }}>
              <HighlightedText text={title} />
            </Typography>
            {hasTooltip && !!tooltipLabel ? <InfoTooltip label={tooltipLabel} /> : null}
          </Stack>

          {shouldShowSector ? (
            <Stack
              sx={{
                display: 'flow-root !important',
              }}
            >
              <Box
                bgcolor={colors.kpiTile?.background?.card}
                sx={{
                  color: colors.kpiTile?.text?.main,
                  borderRadius: '16px',
                  width: 'auto',
                  float: 'right',
                  display: 'flex',
                  marginTop: '-36px',
                  height: 'auto',
                  alignItems: 'center',
                  padding: '5px',
                }}
              >
                <Typography variant="bodyText" sx={{ textAlign: 'center' }}>
                  {sectorLabel}
                </Typography>
              </Box>
            </Stack>
          ) : null}

          <Stack
            minHeight="43px"
            marginTop={isHappySignal ? '50px' : ''}
            flexGrow={1}
            justifyContent="center"
          >
            {!isLoading ? (
              <Stack color={resolvedColor} direction="column" alignItems="start">
                <Stack direction="row" alignItems="baseline" flexWrap="wrap">
                  {renderNumbersBlock()}
                  {renderUnitIcon()}
                </Stack>
              </Stack>
            ) : (
              <LoaderComponent />
            )}
          </Stack>

          {shouldShowSubText ? (
            <Box sx={{ maxWidth: '250px' }}>
              <Tooltip title={subText}>
                <Typography
                  variant="bodyText"
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color: resolvedColor,
                  }}
                >
                  {subText}
                </Typography>
              </Tooltip>
            </Box>
          ) : null}

          {isInProgress ? (
            <Box position="absolute" right={15} bottom={10}>
              <Tooltip title="In Progress/Validation">
                <img
                  src={NoPrgramFound}
                  alt="in-progress"
                  style={{
                    height: '20px',
                    width: '20px',
                  }}
                />
              </Tooltip>
            </Box>
          ) : null}

          {renderChangeSection()}
        </Stack>
      </Stack>
    </Box>
  );
};

export default KpiTile;
